import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Formik, Form, Field } from 'formik';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { withGrowl } from '../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { growlShape } from '../../components/prop-types';
import { InputNumberComponent } from '../../components/prime-formik';

const messages = defineMessages({
  required: {
    id: 'errors.validation.required',
    defaultMessage: 'Required',
  },
  header: {
    id: 'update-conversion-norm.header',
    defaultMessage: 'Update conversion norm',
  },
  one: {
    id: 'update-conversion-norm.one',
    defaultMessage: 'One',
  },
  two: {
    id: 'update-conversion-norm.two',
    defaultMessage: 'Two',
  },
  three: {
    id: 'update-conversion-norm.three',
    defaultMessage: 'Three',
  },
  four: {
    id: 'update-conversion-norm.four',
    defaultMessage: 'Four',
  },
  five: {
    id: 'update-conversion-norm.five',
    defaultMessage: 'Five',
  },
  six: {
    id: 'update-conversion-norm.six',
    defaultMessage: 'Six',
  },
  seven: {
    id: 'update-conversion-norm.seven',
    defaultMessage: 'Seven',
  },
  eight: {
    id: 'update-conversion-norm.eight',
    defaultMessage: 'Eight',
  },
  nine: {
    id: 'update-conversion-norm.nine',
    defaultMessage: 'Nine',
  },
  ten: {
    id: 'update-conversion-norm.ten',
    defaultMessage: 'Ten',
  },
});

const UPDATE_CONVERSION_NORM_MUTATION = gql`
  mutation updateConversionNorm($input: UpdateConversionNormInput!) {
    updateConversionNorm(input: $input) {
      sectionCode
      dimensionCode
      conversionNorm
      language
    }
  }
`;

const UpdateConversionNorm = ({
  accountId, growl, dialog, hideDialog,
}) => {
  const { formatMessage } = useIntl();

  const [updateConversionNormMutation] = useMutation(UPDATE_CONVERSION_NORM_MUTATION);

  const {
    visible, sectionCode, dimensionCode, conversionNorm, language,
  } = dialog;


  const initialValues = {
    one: conversionNorm ? conversionNorm[0] : 0,
    two: conversionNorm ? conversionNorm[1] : 0,
    three: conversionNorm ? conversionNorm[2] : 0,
    four: conversionNorm ? conversionNorm[3] : 0,
    five: conversionNorm ? conversionNorm[4] : 0,
    six: conversionNorm ? conversionNorm[5] : 0,
    seven: conversionNorm ? conversionNorm[6] : 0,
    eight: conversionNorm ? conversionNorm[7] : 0,
    nine: conversionNorm ? conversionNorm[8] : 0,
    ten: conversionNorm ? conversionNorm[9] : 0,
  };

  const validationSchema = Yup.object().shape({
    one: Yup.number().required(formatMessage(messages.required)),
    two: Yup.number().required(formatMessage(messages.required)),
    three: Yup.number().required(formatMessage(messages.required)),
    four: Yup.number().required(formatMessage(messages.required)),
    five: Yup.number().required(formatMessage(messages.required)),
    six: Yup.number().required(formatMessage(messages.required)),
    seven: Yup.number().required(formatMessage(messages.required)),
    eight: Yup.number().required(formatMessage(messages.required)),
    nine: Yup.number().required(formatMessage(messages.required)),
    ten: Yup.number().required(formatMessage(messages.required)),
  });

  const updateConversionNorm = async (values, resetForm) => {
    const {
      one, two, three, four, five, six, seven, eight, nine, ten,
    } = values;

    const conversionNormToSave = [one, two, three, four, five, six, seven, eight, nine, ten];

    try {
      await updateConversionNormMutation({
        variables: {
          input: {
            accountId,
            sectionCode,
            dimensionCode,
            conversionNorm: conversionNormToSave,
            language,
          },
        },
        refetchQueries: ['contentCategoryAndActiveLocales'],
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      resetForm();
      hideDialog();
    }
  };

  return (
    <div className="conversion-norm-dialog">
      <Dialog
        visible={visible}
        header={formatMessage(messages.header)}
        onHide={hideDialog}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
            updateConversionNorm(values, resetForm);
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <div>
                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.one)}</div>
                  <div>
                    <Field id="one" name="one" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.two)}</div>
                  <div>
                    <Field id="two" name="two" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.three)}</div>
                  <div>
                    <Field id="three" name="three" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.four)}</div>
                  <div>
                    <Field id="four" name="four" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.five)}</div>
                  <div>
                    <Field id="five" name="five" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.six)}</div>
                  <div>
                    <Field id="six" name="six" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.seven)}</div>
                  <div>
                    <Field id="seven" name="seven" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.eight)}</div>
                  <div>
                    <Field id="eight" name="eight" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.nine)}</div>
                  <div>
                    <Field id="nine" name="nine" component={InputNumberComponent} />
                  </div>
                </div>

                <div className="conversion-norm-input">
                  <div>{formatMessage(messages.ten)}</div>
                  <div>
                    <Field id="ten" name="ten" component={InputNumberComponent} />
                  </div>
                </div>

              </div>
              <div className="buttons">
                <FormattedMessage id="actions.save" defaultMessage="Save">
                  {text => <Button type="submit" label={text} disabled={isSubmitting} />}
                </FormattedMessage>
                <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
                  {text => <Button type="button" label={text} className="p-button-secondary" onClick={hideDialog} />}
                </FormattedMessage>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

UpdateConversionNorm.propTypes = {
  accountId: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
  dialog: PropTypes.objectOf(PropTypes.any).isRequired,
  hideDialog: PropTypes.func.isRequired,
};

export default withGrowl(UpdateConversionNorm);
